// Hook que carga una sesión por id. El wrapper de SesionCamara lo usa
// para resolver `ejercicio_id` y `estado` antes de renderizar la pantalla.
// Refresh-safe: la URL contiene el sessionId, esta query rehidrata todo
// el contexto necesario.

import { useEffect, useState } from "react";
import { supabase } from "../client";

/** Validación defensiva de formato UUID antes del query. URLs malformadas
 *  (bookmark del routing pre-bloque B con ejercicio_id en lugar de UUID,
 *  deep-link copiado, escritura manual) llegaban al .eq("id", ...) y
 *  Postgres tiraba 22P02. Mejor: early-return con not-found. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type SesionEstado =
  | "en_progreso"
  | "completada"
  | "cancelada"
  | "interrumpida";

export interface SesionRow {
  id: string;
  ejercicio_id: string;
  estado: SesionEstado;
}

interface State {
  data: SesionRow | null;
  /** "not-found" cuando el id no existe; "fetch-error" para network/SQL.
   *  null mientras está loading o hubo éxito. Nos importa distinguir
   *  para mostrar mensajes distintos en el wrapper. */
  errorKind: "not-found" | "fetch-error" | null;
  errorMessage: string | null;
  loading: boolean;
}

export function useSesion(sessionId: string | undefined): State {
  const [state, setState] = useState<State>({
    data: null,
    errorKind: null,
    errorMessage: null,
    loading: true,
  });

  useEffect(() => {
    if (!sessionId) {
      setState({
        data: null,
        errorKind: "not-found",
        errorMessage: "Falta el id de sesión en la URL",
        loading: false,
      });
      return;
    }

    if (!UUID_RE.test(sessionId)) {
      setState({
        data: null,
        errorKind: "not-found",
        errorMessage: null,
        loading: false,
      });
      return;
    }

    let cancelled = false;
    setState({
      data: null,
      errorKind: null,
      errorMessage: null,
      loading: true,
    });

    (async () => {
      const { data, error } = await supabase
        .from("sesiones")
        .select("id, ejercicio_id, estado")
        .eq("id", sessionId)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.warn("[useSesion] fetch error", error);
        setState({
          data: null,
          errorKind: "fetch-error",
          errorMessage: error.message,
          loading: false,
        });
        return;
      }

      if (!data) {
        setState({
          data: null,
          errorKind: "not-found",
          errorMessage: null,
          loading: false,
        });
        return;
      }

      setState({
        data: data as SesionRow,
        errorKind: null,
        errorMessage: null,
        loading: false,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return state;
}
